/**
 * MODULE: domain/pages/frontend/api/pagesApi.ts
 * PURPOSE: HTTP client for pages-service API
 * DEPENDENCIES: @shared/lib/frontend/api/config, ../utils/workspaceId
 * PUBLIC_API: pagesApi
 * SIDE_EFFECTS: HTTP to /api/v1/pages
 * AGENT SAFETY: 🟢 SAFE
 * RECENT: 2026-07-06 [2.2.1494] — pagesBaseUrl() at call time (runtime config.js / same-origin `/api` on Azure :80).
 * RECENT_PRIOR: 2026-05-24 [2.2.1137] — resolvePagesWorkspaceId on tree/search/create/migration; Pydantic 422 error formatting.
 */

import { API_CONFIG, apiV1Url, getAuthHeaders } from '@shared/lib/frontend/api/config';
import type {
  Block,
  CreateBlockRequest,
  CreateDatabaseRowRequest,
  CreatePageCommentRequest,
  CreatePageFromTemplateRequest,
  DuplicatePageRequest,
  CreatePageRequest,
  CreatePageViewRequest,
  DatabaseRow,
  FlatNotesMigrationRequest,
  FlatNotesMigrationResult,
  FlatNotesMigrationStatus,
  LegacyNoteRedirect,
  MoveBlockRequest,
  Page,
  PageAiGenerateRequest,
  PageAiGenerateResponse,
  PageMentionSearchResponse,
  PageBacklink,
  PageComment,
  PageCommentNotificationPreferences,
  PageCollaborationSession,
  PageDatabase,
  EffectivePagePermission,
  GrantPagePermissionRequest,
  PageDealLinkItem,
  PageDealRoom,
  PageCrmContext,
  PageDealSummary,
  PageContactPreview,
  PageCompanyPreview,
  PagePermission,
  PageShareCreateResponse,
  PageShareGuestInviteRequest,
  PageShareGuestInviteResponse,
  PageShareSettings,
  PageShareStatus,
  PublicPagePayload,
  PageRevisionDetail,
  PageRevisionListItem,
  PageSearchResult,
  PageTemplate,
  SavePageAsTemplateRequest,
  SavePageAsTemplateResponse,
  PageFavorites,
  SetPageFavoritesRequest,
  PageTreeNode,
  PageView,
  UpdateBlockRequest,
  UpdateDatabaseSchemaRequest,
  UpdateDatabaseRowRequest,
  UpdatePageRequest,
} from '../../contracts/pages.types';
import {
  INVALID_PAGES_WORKSPACE_MESSAGE,
  resolvePagesWorkspaceId,
} from '../utils/workspaceId';

/** Resolve at call time so runtime config.js / same-origin `/api` apply on Azure nginx (:80). */
function pagesBaseUrl(): string {
  return `${API_CONFIG.baseURL.replace(/\/$/, '')}/api/v1/pages`;
}

export type PublicPageShareErrorCode = 'not_found' | 'expired';

export class PublicPageShareError extends Error {
  readonly code: PublicPageShareErrorCode;

  constructor(code: PublicPageShareErrorCode) {
    super(code);
    this.name = 'PublicPageShareError';
    this.code = code;
  }
}

function formatApiErrorBody(body: unknown, status: number): string {
  if (typeof body === 'object' && body !== null && 'detail' in body) {
    const detail = (body as { detail: unknown }).detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((item) => {
          if (typeof item === 'object' && item !== null && 'msg' in item) {
            return String((item as { msg: unknown }).msg);
          }
          return String(item);
        })
        .join('; ');
    }
  }
  return `HTTP ${status}`;
}

function requirePagesWorkspaceId(workspaceId: string): string {
  const resolved = resolvePagesWorkspaceId(workspaceId);
  if (!resolved) {
    throw new Error(INVALID_PAGES_WORKSPACE_MESSAGE);
  }
  return resolved;
}

async function fetchWithAuth<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...options.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(formatApiErrorBody(error, response.status));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

export const pagesApi = {
  getTree: (workspaceId: string) => {
    const resolved = resolvePagesWorkspaceId(workspaceId);
    if (!resolved) return Promise.resolve([] as PageTreeNode[]);
    return fetchWithAuth<PageTreeNode[]>(
      `${pagesBaseUrl()}/tree?workspace_id=${encodeURIComponent(resolved)}`
    );
  },

  getArchivedPages: (workspaceId: string) => {
    const resolved = resolvePagesWorkspaceId(workspaceId);
    if (!resolved) return Promise.resolve([] as PageTreeNode[]);
    return fetchWithAuth<PageTreeNode[]>(
      `${pagesBaseUrl()}/archived?workspace_id=${encodeURIComponent(resolved)}`
    );
  },

  searchPages: (workspaceId: string, query: string, limit = 20) => {
    const resolved = resolvePagesWorkspaceId(workspaceId);
    if (!resolved) return Promise.resolve([] as PageSearchResult[]);
    return fetchWithAuth<PageSearchResult[]>(
      `${pagesBaseUrl()}/search?workspace_id=${encodeURIComponent(resolved)}&q=${encodeURIComponent(query)}&limit=${limit}`
    );
  },

  getPage: (pageId: string) => fetchWithAuth<Page>(`${pagesBaseUrl()}/${pageId}`),

  getPageBacklinks: (pageId: string) =>
    fetchWithAuth<PageBacklink[]>(`${pagesBaseUrl()}/${pageId}/backlinks`),

  getPageRevisions: (pageId: string, limit = 20) =>
    fetchWithAuth<PageRevisionListItem[]>(
      `${pagesBaseUrl()}/${pageId}/revisions?limit=${encodeURIComponent(String(limit))}`
    ),

  getPageRevision: (pageId: string, revisionId: string) =>
    fetchWithAuth<PageRevisionDetail>(`${pagesBaseUrl()}/${pageId}/revisions/${revisionId}`),

  savePageRevision: (pageId: string) =>
    fetchWithAuth<PageRevisionListItem>(`${pagesBaseUrl()}/${pageId}/revisions`, { method: 'POST' }),

  restorePageRevision: (pageId: string, revisionId: string) =>
    fetchWithAuth<Page>(`${pagesBaseUrl()}/${pageId}/revisions/${revisionId}/restore`, {
      method: 'POST',
    }),

  getPageDealRoom: (pageId: string) =>
    fetchWithAuth<PageDealRoom>(`${pagesBaseUrl()}/${pageId}/deal-room`),

  getPageCrmContext: (pageId: string) =>
    fetchWithAuth<PageCrmContext>(`${pagesBaseUrl()}/${pageId}/crm-context`),

  getCrmDealPreview: (dealId: string) =>
    fetchWithAuth<PageDealSummary>(`${pagesBaseUrl()}/crm/deals/${dealId}/preview`),

  getCrmContactPreview: (contactId: string) =>
    fetchWithAuth<PageContactPreview>(`${pagesBaseUrl()}/crm/contacts/${contactId}/preview`),

  getCrmCompanyPreview: (companyId: string) =>
    fetchWithAuth<PageCompanyPreview>(`${pagesBaseUrl()}/crm/accounts/${companyId}/preview`),

  getPagesByDeal: (dealId: string, workspaceId?: string) => {
    const params = workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : '';
    return fetchWithAuth<PageDealLinkItem[]>(`${pagesBaseUrl()}/by-deal/${dealId}${params}`);
  },

  getPagesByCompany: (companyId: string, workspaceId?: string) => {
    const params = workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : '';
    return fetchWithAuth<PageDealLinkItem[]>(`${pagesBaseUrl()}/by-company/${companyId}${params}`);
  },

  getPagesByContact: (contactId: string, workspaceId?: string) => {
    const params = workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : '';
    return fetchWithAuth<PageDealLinkItem[]>(`${pagesBaseUrl()}/by-contact/${contactId}${params}`);
  },

  createPage: (data: CreatePageRequest) => {
    const workspace_id = requirePagesWorkspaceId(data.workspace_id);
    return fetchWithAuth<Page>(pagesBaseUrl(), {
      method: 'POST',
      body: JSON.stringify({ ...data, workspace_id }),
    });
  },

  updatePage: (pageId: string, data: UpdatePageRequest) =>
    fetchWithAuth<Page>(`${pagesBaseUrl()}/${pageId}`, { method: 'PATCH', body: JSON.stringify(data) }),

  deletePage: (pageId: string) =>
    fetchWithAuth<void>(`${pagesBaseUrl()}/${pageId}`, { method: 'DELETE' }),

  duplicatePage: (pageId: string, data: DuplicatePageRequest = {}) =>
    fetchWithAuth<Page>(`${pagesBaseUrl()}/${pageId}/duplicate`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getPageTemplates: (workspaceId?: string) => {
    const params = workspaceId
      ? `?workspace_id=${encodeURIComponent(requirePagesWorkspaceId(workspaceId))}`
      : '';
    return fetchWithAuth<PageTemplate[]>(`${pagesBaseUrl()}/templates${params}`);
  },

  savePageAsTemplate: (pageId: string, data: SavePageAsTemplateRequest) =>
    fetchWithAuth<SavePageAsTemplateResponse>(`${pagesBaseUrl()}/${pageId}/save-as-template`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deletePageTemplate: (templateId: string) =>
    fetchWithAuth<void>(`${pagesBaseUrl()}/templates/${encodeURIComponent(templateId)}`, {
      method: 'DELETE',
    }),

  createPageFromTemplate: (data: CreatePageFromTemplateRequest) => {
    const workspace_id = requirePagesWorkspaceId(data.workspace_id);
    return fetchWithAuth<Page>(`${pagesBaseUrl()}/from-template`, {
      method: 'POST',
      body: JSON.stringify({ ...data, workspace_id }),
    });
  },

  getPageFavorites: (workspaceId: string) => {
    const resolved = requirePagesWorkspaceId(workspaceId);
    return fetchWithAuth<PageFavorites>(
      `${pagesBaseUrl()}/favorites?workspace_id=${encodeURIComponent(resolved)}`
    );
  },

  setPageFavorites: (workspaceId: string, data: SetPageFavoritesRequest) => {
    const resolved = requirePagesWorkspaceId(workspaceId);
    return fetchWithAuth<PageFavorites>(`${pagesBaseUrl()}/favorites?workspace_id=${encodeURIComponent(resolved)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  getMyPagePermission: (pageId: string) =>
    fetchWithAuth<EffectivePagePermission>(`${pagesBaseUrl()}/${pageId}/permissions/me`),

  getPagePermissions: (pageId: string) =>
    fetchWithAuth<PagePermission[]>(`${pagesBaseUrl()}/${pageId}/permissions`),

  grantPagePermission: (pageId: string, data: GrantPagePermissionRequest) =>
    fetchWithAuth<PagePermission>(`${pagesBaseUrl()}/${pageId}/permissions`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  revokePagePermission: (pageId: string, granteeUserId: string) =>
    fetchWithAuth<void>(`${pagesBaseUrl()}/${pageId}/permissions/${granteeUserId}`, {
      method: 'DELETE',
    }),

  getPageShareStatus: (pageId: string) =>
    fetchWithAuth<PageShareStatus>(`${pagesBaseUrl()}/${pageId}/share`),

  ensurePageShare: (pageId: string, settings: PageShareSettings = {}) =>
    fetchWithAuth<PageShareCreateResponse>(`${pagesBaseUrl()}/${pageId}/share`, {
      method: 'POST',
      body: JSON.stringify(settings),
    }),

  updatePageShareExpiry: (pageId: string, settings: PageShareSettings) =>
    fetchWithAuth<PageShareCreateResponse>(`${pagesBaseUrl()}/${pageId}/share`, {
      method: 'PATCH',
      body: JSON.stringify(settings),
    }),

  revokePageShare: (pageId: string) =>
    fetchWithAuth<void>(`${pagesBaseUrl()}/${pageId}/share`, {
      method: 'DELETE',
    }),

  inviteGuestToPageShare: (pageId: string, payload: PageShareGuestInviteRequest) =>
    fetchWithAuth<PageShareGuestInviteResponse>(`${pagesBaseUrl()}/${pageId}/share/invite`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /** GET /pages/share/public/:token — anonymous; no Authorization header. */
  getPublicPageByShareToken: async (shareToken: string): Promise<PublicPagePayload> => {
    const url = apiV1Url(`/pages/share/public/${encodeURIComponent(shareToken.trim())}`);
    const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
    if (res.status === 410) {
      throw new PublicPageShareError('expired');
    }
    if (!res.ok) {
      throw new PublicPageShareError('not_found');
    }
    return (await res.json()) as PublicPagePayload;
  },

  getPageComments: (pageId: string, blockId?: string | null) => {
    const params =
      blockId != null && blockId !== ''
        ? `?block_id=${encodeURIComponent(blockId)}`
        : '';
    return fetchWithAuth<PageComment[]>(`${pagesBaseUrl()}/${pageId}/comments${params}`);
  },

  createPageComment: (pageId: string, data: CreatePageCommentRequest) =>
    fetchWithAuth<PageComment>(`${pagesBaseUrl()}/${pageId}/comments`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deletePageComment: (pageId: string, commentId: string) =>
    fetchWithAuth<void>(`${pagesBaseUrl()}/${pageId}/comments/${commentId}`, {
      method: 'DELETE',
    }),

  resolvePageComment: (pageId: string, commentId: string, resolved: boolean) =>
    fetchWithAuth<PageComment>(`${pagesBaseUrl()}/${pageId}/comments/${commentId}/resolve`, {
      method: 'PATCH',
      body: JSON.stringify({ resolved }),
    }),

  getPageCommentNotificationPreferences: () =>
    fetchWithAuth<PageCommentNotificationPreferences>(`${pagesBaseUrl()}/notification-preferences`),

  updatePageCommentNotificationPreferences: (data: PageCommentNotificationPreferences) =>
    fetchWithAuth<PageCommentNotificationPreferences>(`${pagesBaseUrl()}/notification-preferences`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  generatePageAi: (pageId: string, data: PageAiGenerateRequest) =>
    fetchWithAuth<PageAiGenerateResponse>(`${pagesBaseUrl()}/${pageId}/ai-generate`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  searchMentions: (params: { q: string; workspace_id: string }) => {
    const workspace_id = requirePagesWorkspaceId(params.workspace_id);
    const query = new URLSearchParams({
      q: params.q,
      workspace_id,
    });
    return fetchWithAuth<PageMentionSearchResponse>(`${pagesBaseUrl()}/mentions?${query.toString()}`);
  },

  getBlocks: (pageId: string) => fetchWithAuth<Block[]>(`${pagesBaseUrl()}/${pageId}/blocks`),

  createBlock: (pageId: string, data: CreateBlockRequest) =>
    fetchWithAuth<Block>(`${pagesBaseUrl()}/${pageId}/blocks`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateBlock: (blockId: string, data: UpdateBlockRequest) =>
    fetchWithAuth<Block>(`${pagesBaseUrl()}/blocks/${blockId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  moveBlock: (blockId: string, data: MoveBlockRequest) =>
    fetchWithAuth<Block>(`${pagesBaseUrl()}/blocks/${blockId}/move`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteBlock: (blockId: string) =>
    fetchWithAuth<void>(`${pagesBaseUrl()}/blocks/${blockId}`, { method: 'DELETE' }),

  updateDatabaseSchema: (pageId: string, data: UpdateDatabaseSchemaRequest) =>
    fetchWithAuth<PageDatabase>(`${pagesBaseUrl()}/${pageId}/database/schema`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getDatabaseSchema: (pageId: string) =>
    fetchWithAuth<PageDatabase>(`${pagesBaseUrl()}/${pageId}/database/schema`),

  getRows: (pageId: string) =>
    fetchWithAuth<DatabaseRow[]>(`${pagesBaseUrl()}/${pageId}/database/rows`),

  createRow: (pageId: string, data: CreateDatabaseRowRequest) =>
    fetchWithAuth<DatabaseRow>(`${pagesBaseUrl()}/${pageId}/database/rows`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateRow: (pageId: string, rowId: string, data: UpdateDatabaseRowRequest) =>
    fetchWithAuth<DatabaseRow>(`${pagesBaseUrl()}/${pageId}/database/rows/${rowId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteRow: (pageId: string, rowId: string) =>
    fetchWithAuth<void>(`${pagesBaseUrl()}/${pageId}/database/rows/${rowId}`, { method: 'DELETE' }),

  getViews: (pageId: string) => fetchWithAuth<PageView[]>(`${pagesBaseUrl()}/${pageId}/views`),

  createView: (pageId: string, data: CreatePageViewRequest) =>
    fetchWithAuth<PageView>(`${pagesBaseUrl()}/${pageId}/views`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateView: (pageId: string, viewId: string, data: Partial<CreatePageViewRequest>) =>
    fetchWithAuth<PageView>(`${pagesBaseUrl()}/${pageId}/views/${viewId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  getFlatNotesMigrationStatus: (workspaceId?: string) => {
    const resolved = workspaceId ? resolvePagesWorkspaceId(workspaceId) : undefined;
    if (workspaceId && !resolved) {
      return Promise.resolve({
        notes_table_exists: false,
        workspace_id: workspaceId,
        total_notes: 0,
        migrated_count: 0,
        unmigrated_count: 0,
      } satisfies FlatNotesMigrationStatus);
    }
    const query = resolved ? `?workspace_id=${encodeURIComponent(resolved)}` : '';
    return fetchWithAuth<FlatNotesMigrationStatus>(`${pagesBaseUrl()}/migrate-flat-notes/status${query}`);
  },

  migrateFlatNotes: (data: FlatNotesMigrationRequest) => {
    const resolved = data.workspace_id ? resolvePagesWorkspaceId(data.workspace_id) : undefined;
    if (data.workspace_id && !resolved) {
      throw new Error(INVALID_PAGES_WORKSPACE_MESSAGE);
    }
    return fetchWithAuth<FlatNotesMigrationResult>(`${pagesBaseUrl()}/migrate-flat-notes`, {
      method: 'POST',
      body: JSON.stringify(resolved ? { ...data, workspace_id: resolved } : data),
    });
  },

  resolveLegacyNote: (legacyNoteId: string) =>
    fetchWithAuth<LegacyNoteRedirect>(
      `${pagesBaseUrl()}/legacy-notes/${encodeURIComponent(legacyNoteId)}`
    ),

  getCollaborationSession: (pageId: string) =>
    fetchWithAuth<PageCollaborationSession>(`${pagesBaseUrl()}/${pageId}/collaboration/session`),
};
